// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import Select, { SelectProps } from "@insite/mobius/Select";
import LoadShopAccounts from "@pace/App/Handlers/ShopAccounts/LoadShopAccounts";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { getShopAccounts } from "@pace/App/Store/Selectors/GeneralSelectors";
import DynamicDropdown, { OptionObject } from "@pace/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

interface OrderHistorySearchFieldShopAccountState {
    options: OptionObject[];
    account?: string;
}

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState & CustomAppState) => {
    return {
        parameter: state.pages.orderHistory.getOrdersParameter,
        shopAccounts: getShopAccounts(state),
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    loadCurrentShipTos,
    LoadShopAccounts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderHistorySearchFieldShipToStyles {
    select?: SelectProps;
    wrapper?: SearchFieldWrapperStyles;
}

export const shipToStyles: OrderHistorySearchFieldShipToStyles = {};
const styles = shipToStyles;

class OrderHistorySearchFieldShopAccount extends React.Component<Props, OrderHistorySearchFieldShopAccountState> {
    state = {
        options: [],
        account: (this.props.parameter as any)?.billToNumber,
    };

    componentDidMount() {
        if (this.props.shopAccounts.length === 0) {
            this.props.LoadShopAccounts({});
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.shopAccounts !== this.props.shopAccounts) {
            const options: OptionObject[] = this.props.shopAccounts.map(account => ({
                optionText: account.customerLabel,
                optionValue: account.customerNumber,
            }));
            this.setState({
                options
            });
        }
        if((prevProps.parameter as any)?.billToNumber !== (this.props.parameter as any)?.billToNumber) {
            this.setState({
                account: (this.props.parameter as any)?.billToNumber
            });
        }
    }

    handleChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.props.updateSearchFields({ billToNumber: event.currentTarget.value } as any);
    };

    searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        this.props.LoadShopAccounts({
            searchQuery: value
        });
    };

    selectAccountHandler = (account?: string) => {
        this.setState({
            account
        });
        this.props.updateSearchFields({ billToNumber: account } as any);
    };

    render() {
        const shopAccounts = this.props.shopAccounts;

        return (
            <SearchFieldWrapper extendedStyles={styles.wrapper}>
                <DynamicDropdown
                    label={translate("Shop Accounts")}
                    onSelectionChange={this.selectAccountHandler}
                    onInputChange={this.searchTextChanged}
                    selected={this.state.account}
                    options={this.state.options}
                    placeholder={translate("Search or Select Shop Account")}
                    data-test-selector="orderHistory_filterShopAccounts"
                    filterOption={() => true}
                />
            </SearchFieldWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderHistorySearchFieldShopAccount),
    definition: {
        group: "Order History",
        displayName: "Order Shop Account",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
    },
};

export default widgetModule;
